import { EnrollmentStatus } from '@zorro/clients';
import { Card } from '@zorro/design-system/v2/Card';
import {
  Box,
  DonutChart,
  Grid,
  Group,
  Space,
  Stack,
  Text,
  brand,
} from '@zorro/web/design-system';

import { getEnrollmentStatusView } from '../EnrollmentStatus';
import { EnrollmentStatusBadge } from '../EnrollmentStatus/EnrollmentStatusBadge';
import classNames from './CompanyCoverageOverview.module.scss';

const enrollmentStatusToColorMap = new Map<EnrollmentStatus, string>([
  [EnrollmentStatus.PENDING_ELECTION_WINDOW, brand.zorroGray900],
  [EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED, brand.zorroYolk200],
  [EnrollmentStatus.ELECTION_ACTIVE_STARTED, brand.zorroYolk200],
  [EnrollmentStatus.ELECTION_SUBMITTED, brand.primaryButtonSelected],
  [EnrollmentStatus.ENROLLMENT_IN_PROCESS, brand.zorroSky100],
  [EnrollmentStatus.ENROLLMENT_CONFIRMED, brand.zorroGreen300],
  [EnrollmentStatus.WAIVED_ELECTION, brand.zorroGray200],
  [EnrollmentStatus.DEADLINE_PASSED, brand.zorroFire300],
]);

const statusesToDisplay = [...enrollmentStatusToColorMap.keys()];

type BadgeWithCounterProps = {
  enrollmentStatus: EnrollmentStatus;
  count: number;
  year: number;
  onClick: (
    status: EnrollmentStatus,
    year: number,
    params: Record<string, string>
  ) => void;
  params: Record<string, string>;
};

const BadgeWithCounter = ({
  enrollmentStatus,
  count = 0,
  year,
  onClick,
  params,
}: BadgeWithCounterProps) => {
  return (
    <Group
      key={enrollmentStatus}
      gap="xxs"
      wrap="nowrap"
      onClick={() => onClick(enrollmentStatus, year, params)}
      pr="xs"
      w="fit-content"
      classNames={classNames}
    >
      <EnrollmentStatusBadge
        enrollmentStatus={enrollmentStatus}
        className={classNames['badge']}
      />
      <Text size="sm" fw="bold">
        {count}
      </Text>
    </Group>
  );
};

interface CompanyEnrollmentOverviewProps {
  title: string;
  enrollmentYear: number;
  enrollmentCounts: Record<EnrollmentStatus, number>;
  onClick: (status: EnrollmentStatus, year: number) => void;
  params: Record<string, string>;
}

export default function CompanyEnrollmentOverview({
  title,
  enrollmentYear,
  enrollmentCounts,
  onClick,
  params,
}: CompanyEnrollmentOverviewProps) {
  const enrollmentTotal = statusesToDisplay.reduce(
    (total, status) => total + (enrollmentCounts[status] || 0),
    0
  );

  return (
    <Box>
      <Text fw={600} size="md">
        {enrollmentYear} {title}
        <Text c="zorroGray.4" ml="xs">
          {enrollmentTotal} currently in process
        </Text>
      </Text>
      <Space h="md" />
      <Card variant="white-border">
        <Grid align="center">
          <Grid.Col span={4}>
            <Stack gap="xs">
              {statusesToDisplay.slice(0, 4).map((status) => (
                <BadgeWithCounter
                  key={status}
                  enrollmentStatus={status}
                  count={enrollmentCounts[status]}
                  year={enrollmentYear}
                  onClick={onClick}
                  params={params}
                />
              ))}
            </Stack>
          </Grid.Col>
          <Grid.Col span={4} pl="xl">
            <Stack gap="xs">
              {statusesToDisplay.slice(4).map((status) => (
                <BadgeWithCounter
                  key={status}
                  enrollmentStatus={status}
                  count={enrollmentCounts[status]}
                  year={enrollmentYear}
                  onClick={onClick}
                  params={params}
                />
              ))}
            </Stack>
          </Grid.Col>
          <Grid.Col span={4} pl="xl">
            <DonutChart
              data={statusesToDisplay.map((status) => ({
                name: getEnrollmentStatusView(status).label,
                value: enrollmentCounts[status] ?? 0,
                color: enrollmentStatusToColorMap.get(status)!,
              }))}
              tooltipDataSource="segment"
              size={175}
              thickness={22}
              strokeWidth={0}
            />
          </Grid.Col>
        </Grid>
      </Card>
    </Box>
  );
}
